import { Button, Icon } from '@blueprintjs/core';
import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import {
  TEXT_FILTER_MAP,
  TEXT_LIKE_FILTER_MAP,
} from 'Components/Generic/AdvancedDataTable/Helper';
import BOSDateInput from 'Components/Generic/BOSDateInput';
import BOSFilterMultiselect from 'Components/Generic/BOSMultiselect/BOSFilterMultiselect';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import UserMultiselect from 'Components/Generic/BOSMultiselect/UserMultiselect';
import VendorMultiselect from 'Components/Generic/BOSMultiselect/VendorMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import ChargeAllocationStatusLabel from 'Components/Generic/ChargeAllocations/ChargeAllocationStatusLabel';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import { SITE_PAGES } from 'Config';
import ChargeAllocationData from 'Data/ChargeAllocationData';
import { useAlertContext } from 'Data/context/Alerts';
import { useGlobalState } from 'Data/context/GlobalState';
import { getOrgNameFromId } from 'Data/context/Organizations';
import { getUserNameFromId } from 'Data/context/Users';
import { getVendorNameFromId } from 'Data/context/Vendors';
import { user_roles } from 'Data/Enum';
import { TransactionData } from 'Data/TransactionData';
import DataManager from 'Lib/DataManager';
import ShowOnHoverWrapper from 'Lib/hoc/ShowOnHoverWrapper';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatDate } from 'Util/dateUtil';
import {
  CHARGE_ALLOCATION_STATUS,
  getChargeAllocationStatus,
  getChargeAllocationStatusLabel,
  getTransactionCategory,
  getTransactionType,
  TRANSACTION_CATEGORY,
  TRANSACTION_TYPE,
} from './Enum';
import styles from './ReconcileView.module.css';

const TransactionAndChargesContext = createContext(null);
TransactionAndChargesContext.displayName = 'TransactionAndChargesContext';

let DM = new DataManager();

let filterOptions = {
  description: {
    seqRestKey: '*description',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
  trans_type: {
    seqRestKey: '*$transactions.transaction_type',
  },
  status: {
    seqRestKey: '*status',
  },
  organization: {
    seqRestKey: '*$charge_accounts.parent_organization_id',
  },
  vendor: {
    seqRestKey: '*$transactions.vendor_id',
  },
  reference_number: {
    seqRestKey: '*$transactions.reference_number',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  category: {
    seqRestKey: '*category',
  },
  charge_account: {
    seqRestKey: '*$charge_accounts.code',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  expense_code: {
    seqRestKey: '*$expense_codes.expense_code',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  reconciled_by: {
    seqRestKey: '*reconciled_by',
  },
};

export const TransactionAndChargesStateProvider = ({ children }) => {
  const [data, setData] = useState();
  const [globalState] = useGlobalState();
  const [refreshKey, setRefreshKey] = useState(0);

  const [selectedCharges, setSelectedCharges] = useState([]);
  const [chargeEditRow, setChargeEditRow] = useState({
    rowIds: [],
    editColumn: '',
  });
  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const resetState = () => {
    setRefreshKey(0);
    setSelectedCharges([]);
    setChargeEditRow({
      rowIds: [],
      editColumn: '',
    });
  };

  const {
    openConfirmationDialog,
    showConfirmationToast,
    openDeleteDialog,
    showDeletedToast,
  } = useAlertContext();

  useEffect(() => {
    DM.add(
      ChargeAllocationData.registerListener('LIST_LOADED', setData),
      ChargeAllocationData,
    );
    DM.add(
      ChargeAllocationData.registerListener(
        'LIST_LOAD_ERROR',
        onChargeAllocationListError,
      ),
      ChargeAllocationData,
    );
    DM.add(
      ChargeAllocationData.registerListener(
        'RECORDS_UPDATED',
        onChargeAllocationUpdated,
      ),
      ChargeAllocationData,
    );
    DM.add(
      ChargeAllocationData.registerListener(
        'RECORD_UPDATED',
        onChargeAllocationUpdated,
      ),
      ChargeAllocationData,
    );
    DM.add(
      TransactionData.registerListener(
        'RECORD_UPDATED',
        onChargeAllocationUpdated,
      ),
      TransactionData,
    );
    DM.add(
      TransactionData.registerListener('RECORDS_DELETED', onTransactionDeleted),
      TransactionData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    refreshData();
  }, [globalState?.fiscalYear]);

  const onChargeAllocationListError = () => {};
  const clearChargeEditRow = () => {
    setChargeEditRow({ rowIds: [], editColumn: '' });
  };

  const statusClicked = (ca, nextStatus) => {
    let idsToUpdate = selectedCharges?.length ? selectedCharges : [ca.id];

    openConfirmationDialog({
      onConfirm: () =>
        ChargeAllocationData.updateStatuses(idsToUpdate, nextStatus.key),
      dialogTitle:
        ca.status === CHARGE_ALLOCATION_STATUS.UNREVIEWED.key
          ? `Approve ${
              idsToUpdate.length > 1 ? 'all selected charges' : 'single charge'
            }?`
          : `Update ${idsToUpdate.length > 1 ? 'all statuses' : 'status'} to ${
              nextStatus.label
            }?`,
    });
  };
  const clearDateUpdated = (ca, date) => {
    let idsToUpdate = selectedCharges?.length ? selectedCharges : [ca.id];

    openConfirmationDialog({
      onConfirm: () => ChargeAllocationData.updateClearDates(idsToUpdate, date),
      dialogTitle: `Update date cleared${
        idsToUpdate.length > 1 ? ' for all' : ''
      } to ${formatDate(date)}?`,
      dialogMessage: `This will also mark the charge${
        idsToUpdate.length > 1 ? 's' : ''
      } status to cleared, cleared by you.`,
    });
  };
  const postDateUpdated = (ca, date) => {
    let idsToUpdate = selectedCharges?.length ? selectedCharges : [ca.id];

    openConfirmationDialog({
      onConfirm: () =>
        ChargeAllocationData.updatePostedDates(idsToUpdate, date),
      dialogTitle: `Update date posted${
        idsToUpdate.length > 1 ? ' for all' : ''
      } to ${formatDate(date)}?`,
    });
  };

  const updateVendor = (ca, vendor) => {
    if (!vendor?.id || !ca.transaction?.id) return;

    openConfirmationDialog({
      onConfirm: () =>
        TransactionData.updateRecord({
          id: ca.transaction.id,
          vendor_id: vendor.id,
        }),
      dialogTitle: `Update vendor to ${vendor.name}?`,
      dialogMessage: (
        <div style={{ textAlign: 'center' }}>
          (For this transaction and the charges associated with it only)
        </div>
      ),
    });
  };
  const updateOrg = (ca, org) => {
    if (!org?.id || !ca.transaction?.id) return;

    openConfirmationDialog({
      onConfirm: () => {
        TransactionData.updateRecord(
          {
            id: ca.transaction.id,
            parent_organization_id: org.id,
          },
          'IGNORE',
        );
        ChargeAllocationData.updateRecord({
          id: ca.id,
          assigned_charge_account_id: null,
        });
      },
      dialogTitle: `Update core to ${org.name}? `,
      dialogMessage: (
        <div style={{ textAlign: 'center' }}>
          (For this transaction and the charges associated with it only)
        </div>
      ),
    });
  };

  const onChargeAllocationUpdated = (payload) => {
    if (payload) {
      showConfirmationToast(
        `Charge Allocation${payload.length > 1 ? 's' : ''} Updated.`,
      );
      clearChargeEditRow();
      refreshData();
      clearSelectedCharges();
    }
  };

  const toggleParentCheckbox = () => {
    if (selectedCharges.length > 0) {
      clearSelectedCharges();
    }
  };

  const clearSelectedCharges = () => {
    setSelectedCharges([]);
  };
  const toggleChargeSelected = (chargeAllocation) => {
    if (selectedCharges.includes(chargeAllocation.id)) {
      setSelectedCharges(
        selectedCharges.filter((id) => id !== chargeAllocation.id),
      );
    } else {
      setSelectedCharges([...selectedCharges, chargeAllocation.id]);
    }
  };

  const deleteChargeAllocation = (ca) => {
    TransactionData.deleteTransaction(ca.transaction);
  };

  const deleteClicked = (ca) => {
    openDeleteDialog({
      objectType: 'Transaction',
      onConfirmDelete: () => deleteChargeAllocation(ca),
      dialogMessage: (
        <ul style={{ listStyle: 'disc' }}>
          <li>Any line item(s) present will be unassigned.</li>
          <li>Associated charge allocation(s) will be deleted.</li>
        </ul>
      ),
    });
  };

  const onTransactionDeleted = () => {
    showDeletedToast('Transaction');
    refreshData();
    clearSelectedCharges();
  };

  const EditableFieldWrapper = ({ ca, fieldName, fieldText, children }) => {
    return (
      <span
        className={styles.date_box}
        style={
          selectedCharges?.length === 0 || selectedCharges.includes(ca.id)
            ? { cursor: 'pointer' }
            : {}
        }
        onDoubleClick={
          selectedCharges?.length === 0 || selectedCharges.includes(ca.id)
            ? () =>
                setChargeEditRow({
                  rowIds: [ca.id],
                  editColumn: fieldName,
                })
            : undefined
        }>
        {(chargeEditRow.editColumn !== fieldName ||
          !chargeEditRow.rowIds.includes(ca.id)) && (
          <ShowOnHoverWrapper hoverOverChild={fieldText}>
            {selectedCharges?.length === 0 ||
            selectedCharges.includes(ca.id) ? (
              <Button
                small
                className={
                  selectedCharges?.includes(ca.id)
                    ? styles.edit_button_white
                    : styles.edit_button
                }
                icon={'edit'}
                onClick={
                  selectedCharges?.length === 0 ||
                  selectedCharges.includes(ca.id)
                    ? () =>
                        setChargeEditRow({
                          rowIds: [ca.id],
                          editColumn: fieldName,
                        })
                    : undefined
                }
              />
            ) : undefined}
          </ShowOnHoverWrapper>
        )}
        {chargeEditRow.editColumn === fieldName &&
          chargeEditRow.rowIds.includes(ca.id) && (
            <>
              {children}
              <Button
                minimal
                small
                className={styles.clear_edit_date_button}
                icon={<Icon size={14} icon={'cross'} />}
                onClick={() => clearChargeEditRow()}
              />
            </>
          )}
      </span>
    );
  };

  EditableFieldWrapper.propTypes = {
    ca: PropTypes.shape({ id: PropTypes.number }),
    fieldName: PropTypes.string,
    fieldText: PropTypes.string,
    children: PropTypes.element,
  };

  const getHeaders = ({ persistentFilter, updateFilterField, fromState }) => {
    return {
      REF_NUMBER: {
        label: 'Ref #',
        value: 'reference_number',
        fieldName: 'transaction',
        width: 120,
        filterable: true,
        renderHeader() {
          return (
            <DataTableHeaderTextFieldFilter
              fieldName={'Ref #'}
              defaultValue={persistentFilter?.reference_number}
              onFilterValueUpdate={(e) =>
                updateFilterField('reference_number', e.target.value)
              }
            />
          );
        },
        render(chargeAllocation) {
          return (
            <Link
              to={`${SITE_PAGES.TRANSACTION_DETAIL.to}/${chargeAllocation.parent_transaction_id}`}
              state={fromState}>
              {chargeAllocation.transaction?.reference_number ||
                chargeAllocation.parent_transaction_id}
            </Link>
          );
        },
        getText: (chargeAllocation) => {
          return (
            chargeAllocation.transaction?.reference_number ||
            chargeAllocation.parent_transaction_id
          );
        },
      },

      DATE_ENTERED: {
        label: 'Date Entered',
        value: 'date_posted',
        format: formatDate,
        width: 132,
        minWidth: 120,
        sortable: true,
        render(ca) {
          return (
            <EditableFieldWrapper
              ca={ca}
              fieldName={'date_posted'}
              fieldText={formatDate(ca.date_posted)}>
              <BOSDateInput
                value={ca?.date_posted}
                onChange={(date) => postDateUpdated(ca, date)}
              />
            </EditableFieldWrapper>
          );
        },
        getText: (chargeAllocation) => {
          return formatDate(chargeAllocation.date_posted);
        },
      },

      CORE_FACILITY: {
        label: 'Core Facility',
        value: 'organization',
        fieldName: 'charge_account',
        width: 144,
        filterable: true,
        renderHeader() {
          return (
            <OrganizationMultiselect
              onSelectedOrganizationsUpdate={(orgList) =>
                updateFilterField('organization', orgList)
              }
              defaultSelectedItems={persistentFilter?.organization}
              isHeader
            />
          );
        },
        format: (charge_account) => {
          return (
            <OrgNameWithDrawerButton
              orgId={charge_account?.parent_organization_id}
            />
          );
        },
        getText: (chargeAllocation) => {
          return getOrgNameFromId(
            chargeAllocation.charge_account?.parent_organization_id,
          );
        },
      },

      VENDOR: {
        label: 'Vendor',
        value: 'vendor',
        fieldName: 'transaction',
        width: 180,
        filterable: true,
        renderHeader() {
          return (
            <VendorMultiselect
              onSelectedVendorsUpdate={(vendorList) =>
                updateFilterField('vendor', vendorList)
              }
              defaultSelectedItems={persistentFilter?.vendor}
              isHeader
            />
          );
        },
        format: (transaction) => {
          return (
            <VendorNameWithDrawerButton vendorId={transaction?.vendor_id} />
          );
        },
        getText: (chargeAllocation) => {
          return getVendorNameFromId(chargeAllocation.transaction?.vendor_id);
        },
      },

      DESCRIPTION: {
        label: 'Description',
        value: 'description',
        filterable: true,
        truncated: true,
        minWidth: 180,
        renderHeader() {
          return (
            <DataTableHeaderTextFieldFilter
              fieldName={'Description'}
              defaultValue={persistentFilter?.description}
              onFilterValueUpdate={(e) =>
                updateFilterField('description', e.target.value)
              }
            />
          );
        },
      },

      CATEGORY: {
        label: 'Category',
        value: 'category',
        width: 132,
        filterable: true,
        renderHeader() {
          return (
            <BOSSelect
              listData={Object.entries(TRANSACTION_CATEGORY).map(
                ([key, value]) => {
                  return {
                    id: key,
                    name: value,
                  };
                },
              )}
              labelText='Category'
              onItemSelect={(item) => updateFilterField('category', [item])}
              defaultSelectedId={
                persistentFilter?.category?.length
                  ? persistentFilter.category[0].id
                  : undefined
              }
              isHeader
            />
          );
        },
        format: (category) => {
          return getTransactionCategory(category);
        },
      },

      TYPE: {
        label: 'Type',
        value: 'trans_type',
        fieldName: 'transaction',
        width: 112,
        filterable: true,
        renderHeader() {
          return (
            <BOSFilterMultiselect
              listData={Object.entries(TRANSACTION_TYPE).map(([key, value]) => {
                return {
                  key,
                  value,
                };
              })}
              labelText='Type'
              useKeyValueFields
              onSelectionUpdate={(list) =>
                updateFilterField('trans_type', list)
              }
              defaultSelectedItems={persistentFilter?.trans_type}
              autofocus
              isHeader
            />
          );
        },
        format: (transaction) => {
          return transaction
            ? getTransactionType(transaction?.transaction_type)
            : '';
        },
      },

      ACCOUNT: {
        label: 'Act/Proj',
        value: 'charge_account',
        width: 100,
        filterable: true,
        renderHeader() {
          return (
            <DataTableHeaderTextFieldFilter
              fieldName={'Act/Proj'}
              defaultValue={persistentFilter?.charge_account}
              onFilterValueUpdate={(e) =>
                updateFilterField('charge_account', e.target.value)
              }
            />
          );
        },
        format: (acc) => {
          return acc?.code;
        },
      },

      EXPENSE_CODE: {
        label: 'Exp Code',
        value: 'expense_code',
        width: 100,
        filterable: true,
        renderHeader() {
          return (
            <DataTableHeaderTextFieldFilter
              fieldName={'Exp Code'}
              defaultValue={persistentFilter?.expense_code}
              onFilterValueUpdate={(e) =>
                updateFilterField('expense_code', e.target.value)
              }
            />
          );
        },
        format: (ec) => {
          return ec?.expense_code;
        },
      },

      AMOUNT: {
        label: 'Amount',
        value: 'amount',
        width: 100,
        sortable: true,
        moneyColumn: true,
      },

      DATE_CLEARED: {
        label: 'Date Cleared',
        value: 'date_cleared',
        width: 132,
        minWidth: 120,
        sortable: true,
        hidden: !!(
          persistentFilter?.status?.length &&
          !persistentFilter.status.includes(CHARGE_ALLOCATION_STATUS.CLEARED)
        ),
        render(ca) {
          return (
            <EditableFieldWrapper
              ca={ca}
              fieldName={'date_cleared'}
              fieldText={formatDate(ca.date_cleared)}>
              <BOSDateInput
                value={ca?.date_cleared}
                onChange={(date) => clearDateUpdated(ca, date)}
              />
            </EditableFieldWrapper>
          );
        },
        getText: (chargeAllocation) => {
          return formatDate(chargeAllocation.date_cleared);
        },
      },

      RECONCILER: {
        label: 'Reconciler',
        value: 'reconciled_by',
        width: 140,
        filterable: true,
        hidden: !!(
          persistentFilter?.status?.length &&
          !persistentFilter.status.includes(CHARGE_ALLOCATION_STATUS.CLEARED)
        ),
        renderHeader() {
          return (
            <UserMultiselect
              labelText='Reconciler'
              onSelectedUsersUpdate={(userList) =>
                updateFilterField('reconciled_by', userList)
              }
              defaultSelectedItems={persistentFilter?.reconciled_by}
              userRoles={[user_roles.RECONCILER.key]}
              isHeader
            />
          );
        },
        format: (reconciled_by) => {
          return <UserNameWithDrawerButton userId={reconciled_by} />;
        },
        getText: (chargeAllocation) => {
          return getUserNameFromId(chargeAllocation.reconciled_by);
        },
      },

      STATUS: {
        label: 'Status',
        value: 'status',
        width: 112,
        filterable: true,
        renderHeader() {
          return (
            <BOSFilterMultiselect
              listData={Object.values(CHARGE_ALLOCATION_STATUS)}
              labelText='Status'
              idField='key'
              labelField='label'
              onSelectionUpdate={(statusList) =>
                updateFilterField('status', statusList)
              }
              defaultSelectedItems={persistentFilter?.status}
              isHeader
            />
          );
        },
        render(ca) {
          return (
            <ChargeAllocationStatusLabel
              disabled={
                selectedCharges?.length > 0 && !selectedCharges.includes(ca.id)
              }
              selectedStatus={getChargeAllocationStatus(ca.status)}
              onSelectStatus={(nextStatus) => {
                statusClicked(ca, nextStatus);
              }}
            />
          );
        },
        getText: (chargeAllocation) => {
          return getChargeAllocationStatusLabel(chargeAllocation.status);
        },
      },

      DELETE: {
        label: '',
        value: '',
        isIconCol: true,
        render(ca) {
          return (
            <DeleteButton
              miniVersion
              onDeleteItem={() => {
                deleteClicked(ca);
              }}
              tooltipText={'Delete Transaction'}
            />
          );
        },
      },
    };
  };

  const getDataTable = ({
    title,
    dataKey,
    dataQueryFunction,
    dataExportFunction,
    persistentFilter,
    updateFilterField,
    headers,
  }) => {
    return (
      <AdvancedDataTable
        headers={headers}
        refreshKey={refreshKey}
        title={title}
        dataKey={dataKey}
        data={data}
        dataQueryFunc={dataQueryFunction}
        exportDataFunc={dataExportFunction}
        filterOptions={filterOptions}
        filterBy={persistentFilter}
        updateFilterField={updateFilterField}
        defaultSort={'date_posted$DESC'}
        shrinkToFit={false}
        rerenderDependencies={[selectedCharges, chargeEditRow]}
        includeCheckBoxCol
        selectedItems={selectedCharges}
        onToggleCheckboxForItem={toggleChargeSelected}
        onToggleParentCheckbox={toggleParentCheckbox}
      />
    );
  };

  return (
    <TransactionAndChargesContext.Provider
      value={{
        getHeaders,
        getDataTable,
        resetState,
        statusClicked,
        updateOrg,
        updateVendor,
        EditableFieldWrapper,
      }}>
      {children}
    </TransactionAndChargesContext.Provider>
  );
};

TransactionAndChargesStateProvider.propTypes = {
  children: PropTypes.node,
};

export const useTransactionAndChargeAllocationContext = () =>
  useContext(TransactionAndChargesContext);
